import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ButtonComponent, ConfirmDialogModel, MultiLabelsComponent } from 'wecheer-common';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Receipt } from 'src/app/api/api-campaigns.service.generated';
import { AuthService } from 'wecheer-amplify';
import { RECEIPT_TAGS } from 'src/app/const/receipt-tag';
import { PERMISSION } from 'src/app/const/permission';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';

import { MatInputModule } from '@angular/material/input';
@Component({
  selector: 'app-receipt-lines',
  templateUrl: './receipt-lines.component.html',
  styleUrls: ['./receipt-lines.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    HasAnyPermissionDirective,
    ButtonComponent,
    MultiLabelsComponent
]
})
export class ReceiptLinesComponent {
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  authService = inject(AuthService);

  @Input() set data(value: any) {
    this.category = value?.category;
    if (value?.line_items) {
      this.patchFormValues(value?.line_items);
    }
  }
  @Input() receipt!: Receipt;
  @Output() tagsChanged = new EventEmitter<boolean>();
  @Output() detailsChanged = new EventEmitter<boolean>();

  displayedColumns: string[] = ['category', 'description', 'quantity', 'price', 'discount', 'total', 'action'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  mainForm = new FormGroup({
    lineItems: new FormArray([]),
    note: new FormControl('', [Validators.maxLength(5000)]),
    numberOfStamps: new FormControl()
  });
  category: string;
  newTag: string = '';
  tags: string[] = [];
  isGiveStampsChecked: boolean = false;
  isCheckEligibleChecked: boolean = false;
  isCheckFraudChecked: boolean = false;
  isRejectChecked: boolean = false;
  email: string;
  rejectDisabled: boolean = false;
  giveStampDisabled: boolean = false;
  checkFraudDisabled: boolean = false;
  checkEligibleDisabled: boolean = false;
  readonly permissions = PERMISSION;

  ngOnInit(): void {
    this.updateTagChecks(true);
    const user = this.authService.user;
    this.email = user.signInDetails.loginId;

    if (this.tags?.some(x => x.startsWith(RECEIPT_TAGS.REPROCESS_SUCCESS_TAG) || x.startsWith(RECEIPT_TAGS.REPROCESS_FAIL_TAG))) {
      this.removeTagChecks();
    }
    this.mainForm.valueChanges.subscribe(() => {
      this.detailsChanged.emit(true);
    })
  }
  ngOnChanges(): void {
    this.tags = this.receipt?.tags;
    this.fillReprocessingData();
  }

  patchFormValues(lineItems: any[]) {
    const lineItemsFormArray = this.mainForm.get('lineItems') as FormArray;
    lineItemsFormArray.clear();

    if (lineItems && lineItems.length > 0) {
      lineItems.forEach(key => {
        lineItemsFormArray.push(this.createLineItemFormGroup(key));
      });

      this.dataSource = new MatTableDataSource<any>(lineItemsFormArray.controls);
    }
  }

  createLineItemFormGroup(item: any): FormGroup {
    return new FormGroup({
      id: new FormControl(item.id),
      category: new FormControl(this.category),
      description: new FormControl(item.description, [Validators.maxLength(255)]),
      quantity: new FormControl(item.quantity ?? 0, [Validators.required, Validators.min(0)]),
      price: new FormControl(item.price ?? 0, [Validators.required, Validators.min(0)]),
      discount: new FormControl(item.discount ?? 0, [Validators.required, Validators.min(0), this.discountLessThanPriceValidator()]),
      total: new FormControl(item.total ?? 0)
    });
  }

  discountLessThanPriceValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const price = control.parent?.get('price')?.value;
      const discount = control.value;
      return discount > price ? { discountLessThanPrice: true } : null;
    };
  }

  getLineItemsFormArray(): FormArray {
    return this.mainForm.get('lineItems') as FormArray;
  }

  showSnackBar(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
    });
  }

  addLineItem(): void {
    const lineItemsArray = this.mainForm.get('lineItems') as FormArray;

    const item = new FormGroup({
      id: new FormControl(null),
      category: new FormControl(this.category),
      description: new FormControl(''),
      quantity: new FormControl(0),
      price: new FormControl(0),
      discount: new FormControl(0),
      total: new FormControl(0)
    });

    lineItemsArray.push(item);
    this.dataSource.data = lineItemsArray.controls;
  }

  async removeAllLineItems(): Promise<void> {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure?',
      'Remove All Line Items?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) {
      return;
    }

    (this.mainForm.get('lineItems') as FormArray).clear();

    this.dataSource.data = (this.mainForm.get('lineItems') as FormArray).controls;
  }

  async deleteLineItem(lineItem: any) {
    const dialogModel = ConfirmDialogModel.danger(
      'This action cannot be undone. Are you absolutely sure?',
      'Delete Line Item?'
    );
    const confirmResult = await dialogModel.show(this.dialog);
    if (!confirmResult) {
      return;
    }

    const lineItemsArray = this.mainForm.get('lineItems') as FormArray;
    const index = lineItemsArray.controls.findIndex(control => control.value.id === lineItem.value.id);

    if (index !== -1) {
      lineItemsArray.removeAt(index);
      this.dataSource.data = lineItemsArray.controls;
    }
  }

  updateTagChecks(isInitialState: boolean) {
    this.isCheckEligibleChecked = this.tags?.includes(RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG);
    this.isCheckFraudChecked = this.tags?.includes(RECEIPT_TAGS.REPROCESS_CHECK_FRAUD);
    this.isGiveStampsChecked = this.tags?.some(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG));
    this.isRejectChecked = this.tags?.includes(RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG);
    if (this.tags?.some(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG))) {
      var tags = this.tags.filter(x => x.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG));
      if (tags.length > 0) {
        var numberOfStamps = parseInt(tags[0].substring(tags[0].lastIndexOf('_') + 1));
        this.mainForm.get('numberOfStamps').setValue(numberOfStamps);
      }
    }
    if (this.tags?.includes(RECEIPT_TAGS.REPROCESS_TAG)) {
      this.setButtonDisable(true);
    }
    else {
      this.setButtonDisable(false);
      this.tagsChanged.emit(!isInitialState);
    }
  }

  removeTagChecks() {
    this.isCheckEligibleChecked = false;
    this.isCheckFraudChecked = false;
    this.isGiveStampsChecked = false;
    this.isRejectChecked = false;
  }

  fillReprocessingData() {
    this.tags = this.receipt?.tags;
    this.mainForm.controls['note'].setValue(this.receipt?.note);
  }

  addTag() {
    if (this.newTag.trim()) {
      this.tags.push(this.newTag.trim());
      this.newTag = '';
      this.updateTagChecks(false);
    }
  }

  removeTag(tag: string) {
    this.tags = this.tags.filter(t => t !== tag);
    this.updateTagChecks(false);
  }

  giveStamps() {
    var numberOfStamps = this.mainForm.controls['numberOfStamps'];
    var stamps = numberOfStamps.value;

    const newTag = `${RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG}${stamps}`;
    this.removeTagPreviousAction();
    if (!this.tags.some(tag => tag.startsWith(RECEIPT_TAGS.REPROCESS_MANUAL_GIVE_STAMPS_PREFIX_TAG))) {
      if (!stamps || stamps == 0) {
        numberOfStamps.addValidators(Validators.required);
      } else {
        this.tags.push(newTag);
        this.isGiveStampsChecked = true;
      }
      this.rejectDisabled = true;
    }
    else {
      this.tags = this.tags.filter(item => item !== newTag);
      numberOfStamps.setValue(null);
      numberOfStamps.clearValidators();
      this.isGiveStampsChecked = false;
      this.rejectDisabled = false;
    }
    numberOfStamps.updateValueAndValidity();
    this.tagsChanged.emit(true);
    return;
  }

  checkEligible() {
    const newTag = RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG;
    this.removeTagPreviousAction();
    if (!this.tags.includes(newTag)) {
      this.tags.push(newTag);
      this.isCheckEligibleChecked = true;
      this.rejectDisabled = true;
    }
    else {
      this.tags = this.tags.filter(item => item !== newTag);
      this.isCheckEligibleChecked = false;
      this.rejectDisabled = false;
    }
    this.tagsChanged.emit(true);
    return;
  }

  checkFraud() {
    const tagName = RECEIPT_TAGS.REPROCESS_CHECK_FRAUD;
    if (!this.tags.includes(tagName)) {
      this.tags.push(tagName);
      this.isCheckFraudChecked = true;
      this.rejectDisabled = true;
    }
    else {
      this.tags = this.tags.filter(item => item !== tagName);
      this.isCheckFraudChecked = false;
      this.rejectDisabled = false;
    }
    this.tagsChanged.emit(true);
  }

  reject() {
    const tagName = RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG;
    this.removeTagPreviousAction();
    if (!this.tags.includes(tagName)) {
      this.tags.push(tagName);
      this.isRejectChecked = true;
      this.giveStampDisabled = true;
      this.checkFraudDisabled = true;
      this.checkEligibleDisabled = true;
    } else {
      this.tags = this.tags.filter(item => item !== tagName);
      this.isRejectChecked = false;
      this.giveStampDisabled = false;
      this.checkFraudDisabled = false;
      this.checkEligibleDisabled = false;
    }
    this.tagsChanged.emit(true);
  }

  setButtonDisable(disabled: boolean) {
    this.rejectDisabled = disabled;
    this.checkEligibleDisabled = disabled;
    this.giveStampDisabled = disabled;
    this.checkFraudDisabled = disabled;
  }

  removeTagPreviousAction() {
    this.tags = this.tags.filter(item => ![RECEIPT_TAGS.REPROCESS_MANUAL_REJECT_TAG, RECEIPT_TAGS.REPROCESS_MANUAL_ACCEPT_TAG].includes(item));
  }

}
